import { useRef } from 'react';
import { ArrowLeftRight } from 'lucide-react';
import { motion } from 'framer-motion';
import { bodyToUnit, unitLabel } from '../strength/units';
import { useWeightUnit } from '../../hooks/useWeightUnit';

const API = process.env.REACT_APP_BACKEND_URL;

export const ProgressComparisonSlider = ({
  beforeImage,
  afterImage,
  sliderPosition,
  isDragging,
  onDragStart,
  onDragEnd,
  onDragMove,
  onTouchMove
}) => {
  const { unit } = useWeightUnit();
  const compareRef = useRef(null);

  const handleMouseMove = (e) => {
    if (!isDragging || !compareRef.current) return;
    const rect = compareRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const percentage = Math.max(0, Math.min(100, (x / rect.width) * 100));
    onDragMove(percentage);
  };

  const handleTouchMove = (e) => {
    if (!compareRef.current) return;
    const rect = compareRef.current.getBoundingClientRect();
    const x = e.touches[0].clientX - rect.left;
    const percentage = Math.max(0, Math.min(100, (x / rect.width) * 100));
    onTouchMove(percentage);
  };

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      className="mb-8"
    >
      <div className="bg-black/50 border border-border p-4">
        <h4 className="font-heading text-lg text-primary uppercase tracking-wider mb-4 text-center">
          Your Transformation
        </h4>
        
        {/* Comparison Slider */}
        <div
          ref={compareRef}
          className="relative aspect-[4/3] md:aspect-video overflow-hidden cursor-col-resize select-none"
          onMouseDown={onDragStart}
          onMouseUp={onDragEnd}
          onMouseLeave={onDragEnd}
          onMouseMove={handleMouseMove}
          onTouchMove={handleTouchMove}
          data-testid="comparison-slider"
        >
          {/* After Image (Full) */}
          <img
            src={`${API}${afterImage.image_url}`}
            alt="After"
            className="absolute inset-0 w-full h-full object-cover"
          />
          
          {/* Before Image (Clipped) */}
          <div
            className="absolute inset-0 overflow-hidden"
            style={{ width: `${sliderPosition}%` }}
          >
            <img
              src={`${API}${beforeImage.image_url}`}
              alt="Before"
              className="absolute inset-0 w-full h-full object-cover"
              style={{ minWidth: `${100 / sliderPosition * 100}%` }}
            />
          </div>
          
          {/* Slider Line */}
          <div
            className="absolute top-0 bottom-0 w-1 bg-primary cursor-col-resize"
            style={{ left: `${sliderPosition}%`, transform: 'translateX(-50%)' }}
          >
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 h-10 w-10 bg-primary rounded-full flex items-center justify-center shadow-lg">
              <ArrowLeftRight className="h-5 w-5 text-black" />
            </div>
          </div>
          
          {/* Labels */}
          <div className="absolute top-4 left-4 px-3 py-1 bg-blue-500/80 text-white text-sm font-heading uppercase tracking-wider">
            Before
          </div>
          <div className="absolute top-4 right-4 px-3 py-1 bg-green-500/80 text-white text-sm font-heading uppercase tracking-wider">
            After
          </div>
        </div>
        
        {/* Comparison Details */}
        <div className="grid grid-cols-2 gap-4 mt-4">
          <div className="text-center p-3 bg-blue-500/10 border border-blue-500/30">
            <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Before</p>
            <p className="text-white">{new Date(beforeImage.created_at).toLocaleDateString()}</p>
            {beforeImage.weight_at_time && (
              <p className="text-blue-400 font-heading">{bodyToUnit(beforeImage.weight_at_time, unit)} {unitLabel(unit)}</p>
            )}
          </div>
          <div className="text-center p-3 bg-green-500/10 border border-green-500/30">
            <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">After</p>
            <p className="text-white">{new Date(afterImage.created_at).toLocaleDateString()}</p>
            {afterImage.weight_at_time && (
              <p className="text-green-400 font-heading">{bodyToUnit(afterImage.weight_at_time, unit)} {unitLabel(unit)}</p>
            )}
          </div>
        </div>
        
        {/* Weight Change */}
        {beforeImage.weight_at_time && afterImage.weight_at_time && (
          <div className="mt-4 p-4 bg-primary/10 border border-primary/30 text-center">
            <p className="text-muted-foreground text-sm mb-1">Weight Change</p>
            <p className={`font-heading text-3xl ${
              afterImage.weight_at_time < beforeImage.weight_at_time ? 'text-green-400' : 
              afterImage.weight_at_time > beforeImage.weight_at_time ? 'text-red-400' : 'text-white'
            }`}>
              {afterImage.weight_at_time < beforeImage.weight_at_time ? '-' : afterImage.weight_at_time > beforeImage.weight_at_time ? '+' : ''}
              {bodyToUnit(Math.abs(afterImage.weight_at_time - beforeImage.weight_at_time), unit)} {unitLabel(unit)}
            </p>
          </div>
        )}
      </div>
    </motion.div>
  );
};
